import { useLazyQuery, useQuery } from '@apollo/client';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { UserContext } from '@providers/userContextProvider';
import useTextDebounce from '@utils/useTextDebounce';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { Close } from 'hephaestus/webIcons';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';
import { NewOrganizationModal } from './NewOrganizationModal';
import { OrganizationTreeBranch } from './OrganizationTreeBranch';
import {
  FETCH_CHILD_ORGANIZATIONS,
  FETCH_INITIAL_ORGANIZATIONS,
  type OrganizationSwitcherFetchedChild,
  type OrganizationSwitcherParent,
  type OrganizationTreeNode,
  SEARCH_ORGANIZATIONS,
} from './organizationSwitcherModal.query';

export type OrganizationSwitcherModalProp = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const compareByTranslatedName = <T extends { translatedName?: unknown }>(
  translateBackendString: (translatedObject: unknown) => string,
  left: T,
  right: T,
) =>
  translateBackendString(left.translatedName) <
  translateBackendString(right.translatedName)
    ? -1
    : 1;

const toStubNode = (id: string): OrganizationTreeNode => ({
  id,
  open: false,
});

const mapSiblings = (
  translateBackendString: (translatedObject: unknown) => string,
  children: OrganizationSwitcherParent['children'],
  nested?: OrganizationTreeNode,
): OrganizationTreeNode[] =>
  [...children]
    .sort((left, right) =>
      compareByTranslatedName(translateBackendString, left, right),
    )
    .map((child) => {
      if (nested && child.id === nested.id) {
        return nested;
      }

      return {
        id: child.id,
        translatedName: child.translatedName,
        open: false,
        children: child.children.map((grandChild) => toStubNode(grandChild.id)),
      };
    });

const buildInitialTree = (
  translateBackendString: (translatedObject: unknown) => string,
  parents: OrganizationSwitcherParent[],
): OrganizationTreeNode | undefined => {
  let tempParent: OrganizationTreeNode | undefined;

  for (const parent of parents) {
    tempParent = {
      id: parent.id,
      translatedName: parent.translatedName,
      open: true,
      children: mapSiblings(
        translateBackendString,
        parent.children,
        tempParent,
      ),
    };
  }

  return tempParent;
};

const mapFetchedChildren = (
  translateBackendString: (translatedObject: unknown) => string,
  children: OrganizationSwitcherFetchedChild[],
): OrganizationTreeNode[] =>
  [...children]
    .sort((left, right) =>
      compareByTranslatedName(translateBackendString, left, right),
    )
    .map((child) => ({
      id: child.id,
      translatedName: child.translatedName,
      open: false,
      children: child.children.map((grandChild) => toStubNode(grandChild.id)),
    }));

const setBranchOpen = (
  branch: OrganizationTreeNode,
  id: string,
  open: boolean,
  children?: OrganizationTreeNode[],
): OrganizationTreeNode => {
  if (branch.id === id) {
    return {
      ...branch,
      open,
      ...(children ? { children } : {}),
    };
  }

  return {
    ...branch,
    children: branch.children?.map((child) =>
      setBranchOpen(child, id, open, children),
    ),
  };
};

export const OrganizationSwitcherModal = ({
  open,
  setOpen,
}: OrganizationSwitcherModalProp) => {
  const { translateBackendString } = useLocalization();
  const { activeOrganization, setActiveOrganizationId, userContext } =
    useContext(UserContext);
  const accessiblePages = userContext.role?.accessiblePages ?? [];
  const canWriteOrganizations = usePermission('write:organizations');
  const [searchText, setSearchText] = useState('');
  const [localOrgId, setLocalOrgId] = useState(activeOrganization.id);
  const [tree, setTree] = useState<OrganizationTreeNode | undefined>();
  const orgSelectorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('orgSettings');
  const debouncedSearchText = useTextDebounce({
    value: searchText,
    delay: 500,
  });
  const { data, loading } = useQuery(SEARCH_ORGANIZATIONS, {
    variables: {
      input: {
        filter: {
          searchText: debouncedSearchText,
        },
        pagination: {
          perPage: 1000,
          sortField: 'name',
        },
      },
    },
    skip: !debouncedSearchText,
  });

  const { data: initialData } = useQuery(FETCH_INITIAL_ORGANIZATIONS, {
    variables: {
      organizationId: activeOrganization.id!,
    },
    skip: !activeOrganization.id,
    fetchPolicy: 'no-cache',
  });

  const [fetchChildOrganizations] = useLazyQuery(FETCH_CHILD_ORGANIZATIONS);

  const scrollToActive = () => {
    const organizationId = activeOrganization.id;
    const container = orgSelectorRef.current;
    if (!organizationId || !container) return;

    const element = document.getElementById(organizationId);
    if (!element) return;

    const nextTop =
      container.scrollTop +
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top;
    container.scrollTo(0, nextTop);
  };

  useEffect(() => {
    const organization = initialData?.fetchOrganization?.organization;
    if (organization) {
      const nextTree = buildInitialTree(
        translateBackendString,
        organization.parents,
      );
      if (nextTree) {
        setTree(nextTree);
      }
    }
    setTimeout(scrollToActive, 200);
  }, [initialData]);

  useEffect(() => {
    setTimeout(scrollToActive, 200);
  }, [open]);

  const updateTreeOpen = async (id: string) => {
    setTree((prev) => {
      if (!prev) {
        return prev;
      }
      return setBranchOpen(prev, id, true);
    });
    const response = await fetchChildOrganizations({
      variables: {
        organizationId: id,
      },
    });
    const children = mapFetchedChildren(
      translateBackendString,
      response.data?.fetchOrganization.organization.children ?? [],
    );

    setTree((prev) => {
      if (!prev) {
        return prev;
      }
      return setBranchOpen(prev, id, true, children);
    });
  };

  const closeTreeBranch = (id: string) => {
    setTree((prev) => {
      if (!prev) {
        return prev;
      }
      return setBranchOpen(prev, id, false);
    });
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 500, fontSize: '20px' }}>
        {translateBackendString(activeOrganization.translatedName)}
        <IconButton aria-label='close' onClick={() => setOpen(false)}>
          <Close fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers ref={orgSelectorRef} id='orgSelector'>
        <TextField
          leadingIcon='search'
          value={searchText}
          onChange={(event: { target: { value: string } }) => {
            setSearchText(event.target.value);
          }}
          fullWidth
        />
        {searchText.length === 0 ? (
          <RadioGroup
            value={localOrgId}
            onChange={(e) => setLocalOrgId(e.target.value as string)}
          >
            <Stack useFlexGap sx={{ mt: 2, width: '100%' }}>
              {tree && (
                <OrganizationTreeBranch
                  branch={tree}
                  onSelect={setLocalOrgId}
                  onOpen={updateTreeOpen}
                  onClose={closeTreeBranch}
                />
              )}
            </Stack>
          </RadioGroup>
        ) : loading ? (
          <Box sx={{ py: 2 }}>
            <Skeleton
              width='100%'
              height='54px'
              sx={{ mb: 1, borderRadius: theme.borderRadius.md }}
              variant='rectangular'
            />
            <Skeleton
              width='100%'
              height='54px'
              sx={{ mb: 1, borderRadius: theme.borderRadius.md }}
              variant='rectangular'
            />
            <Skeleton
              width='100%'
              height='54px'
              sx={{ mb: 1, borderRadius: theme.borderRadius.md }}
              variant='rectangular'
            />
          </Box>
        ) : (
          <RadioGroup
            value={localOrgId}
            onChange={(e) => setLocalOrgId(e.target.value as string)}
          >
            <Stack
              useFlexGap
              sx={{
                pt: 2,
                width: '100%',
              }}
            >
              {data?.fetchOrganizations?.organizations.map((org) => (
                <Box
                  key={org.id}
                  sx={{
                    px: 2,
                    py: 1,
                    pl: 1,
                    '&:hover': {
                      background: theme.color.background,
                    },

                    borderRadius: theme.borderRadius.md,
                  }}
                >
                  <FormControlLabel
                    value={org.id}
                    control={<Radio />}
                    label={
                      <>
                        {translateBackendString(org.translatedName)}
                        <Typography
                          variant='bodyMedium'
                          sx={{ color: theme.color.onSurfaceVariant }}
                        >
                          {[...org.parents]
                            .filter((parent) => parent.id !== org.id)
                            .reverse()
                            .map((parent) =>
                              translateBackendString(parent.translatedName),
                            )
                            .join(' > ')}
                        </Typography>
                      </>
                    }
                  />
                </Box>
              ))}
            </Stack>
          </RadioGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {location.pathname.includes('/settings') && canWriteOrganizations ? (
            <NewOrganizationModal
              afterCreate={(id: string) => {
                setActiveOrganizationId(id);
                setOpen(false);
                navigate('/settings/general');
              }}
            />
          ) : (
            <Box />
          )}
          <Button
            onClick={() => {
              setActiveOrganizationId(localOrgId);
              setOpen(false);
              if (!location.pathname.includes('settings/')) {
                const firstAccessiblePath =
                  accessiblePages[0].toLocaleLowerCase();
                navigate(`/${firstAccessiblePath}`);
              }
            }}
          >
            {t('changeOrganization')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
